// The itinerary's storefront (CMS) title — one rule, three readers.
//
// `itinerary.cms_title` holds `[{ languageCode, text }]`, edited on the
// settings page's CMS Title tab. Until someone edits it the column is null and
// the title is DERIVED from the plain `title` in the itinerary's own language,
// so it keeps following renames. The CMS sync (writes the CMS row), the
// settings tab (seeds its draft) and the storefront fallback all resolve it
// here, so the three can never disagree about what an unedited itinerary is
// called.

import { languageCodeValues } from '../schemas/facility';

const SUPPORTED = new Set<string>(languageCodeValues);

export type ItineraryCmsTitleEntry = { languageCode: string; text: string };

export type ItineraryCmsTitleSource = {
  title: string | null;
  languageCode: string | null;
  cmsTitle: unknown;
};

const savedEntries = (value: unknown): ItineraryCmsTitleEntry[] => {
  if (!Array.isArray(value)) return [];
  const entries: ItineraryCmsTitleEntry[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const { languageCode, text } = raw as Partial<ItineraryCmsTitleEntry>;
    if (typeof languageCode !== 'string' || typeof text !== 'string') continue;
    if (!languageCode.trim() || !text.trim()) continue;
    entries.push({
      languageCode: languageCode.trim().toLowerCase(),
      text: text.trim(),
    });
  }
  return entries;
};

export const resolveItineraryCmsTitle = (
  source: ItineraryCmsTitleSource
): ItineraryCmsTitleEntry[] => {
  const saved = savedEntries(source.cmsTitle);
  if (saved.length > 0) return saved;
  const title = source.title?.trim();
  if (!title) return [];
  // A code outside the supported set (e.g. legacy `pt-br`) could never be
  // saved from the tab or matched by the storefront — derive it as English.
  const code = source.languageCode?.trim().toLowerCase() ?? '';
  const languageCode = SUPPORTED.has(code) ? code : 'en';
  return [{ languageCode, text: title }];
};
